import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextInput, Textarea, Button, SegmentedControl, NumberInput } from '@mantine/core';
import dayjs from 'dayjs';
import { formatPrice, setWebTitle, formatDay, formatDayShort } from '@/lib/format-utils';
import {
    useServiceGroup,
    useUpdateServiceGroup,
    useAddServiceToGroup,
    useRemoveServiceFromGroup,
    useUpdateServiceGroupLinkDiscount,
    useDeleteServiceGroup,
    useDeleteServiceGroupCover
} from '@/hooks/api-hooks';
import { getServiceGroupTotalPrice, getServiceGroupBasePrice, getServiceSellPrice } from '@/lib/models-utils';
import { ServiceGroup, EMPTY_SERVICE_GROUP, Service } from '@turizi/shared';
import { IconTrash, IconPlus, IconPercentage } from '@tabler/icons-react';
import ServicesListWithFilters from '@/components/services-list-with-filters';
import { MoneyIn, SkeletonList } from '@/components/snippets';
import DrawerPage from '@/components/drawer-page';
import { notify } from '@/lib/notify';
import { SERVICE_GROUPS_PATH } from '@/lib/navigation';
import { useNavigate } from 'react-router-dom';
import ImageUploadPreview from '@/components/image-upload-preview';
import ServiceCardCatalog from '@/components/service-card-catalog';


export default function EditServiceGroupPage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [draft, setDraft] = useState<ServiceGroup>(EMPTY_SERVICE_GROUP);
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [targetPrice, setTargetPrice] = useState<number | ''>('');
    const [distributeMode, setDistributeMode] = useState<'proportional' | 'equal'>('proportional');
    const {
        data,
        isLoading: isLoadingServiceGroup
    } = useServiceGroup(id);
    const serviceGroup = data?.data;
    const update = useUpdateServiceGroup();
    const addService = useAddServiceToGroup();
    const removeService = useRemoveServiceFromGroup();
    const updateDiscount = useUpdateServiceGroupLinkDiscount();
    const deleteServiceGroup = useDeleteServiceGroup();
    const deleteCover = useDeleteServiceGroupCover();
    const navigate = useNavigate();

    useEffect(() => {
        if (serviceGroup) {
            setDraft(serviceGroup);
        }
    }, [serviceGroup]);

    const saveServiceGroup = () => {
        update.mutate(draft);
    };

    const addServiceToGroup = (service: Service) => {
        if (!serviceGroup) return;
        setDrawerOpened(false);
        addService.mutate({
            groupId: id,
            serviceId: service.id,
            discount: 0
        }, {
            onSuccess: () => {
                notify.success(t('service_added_to_group'), t('service_added_to_group_success'));
            },
            onError: () => {
                notify.error(t('service_added_to_group'), t('service_added_to_group_error'));
            }
        });
    };

    const handleDeleteServiceGroup = () => {
        deleteServiceGroup.mutate(id, {
            onSuccess: () => {
                notify.success(t('service_group_deleted'), t('service_group_deleted_success'));
                navigate(SERVICE_GROUPS_PATH);
            },
            onError: () => {
                notify.error(t('service_group_deleted'), t('service_group_deleted_error'));
            }
        });
    };

    const removeServiceFromGroup = (service: Service) => {
        if (!serviceGroup) return;
        removeService.mutate({ groupId: id, serviceId: service.id });
    };

    const handleUpdateDraftDiscount = (linkId: number, discount: number) => {
        const updatedLinks = (draft.service_links || []).map(link =>
            link.id === linkId ? { ...link, discount } : link
        );
        setDraft({ ...draft, service_links: updatedLinks });
    };

    const [isSavingDiscounts, setIsSavingDiscounts] = useState(false);
    const handleSaveAllDiscounts = async () => {
        setIsSavingDiscounts(true);
        try {
            await Promise.all(
                (draft.service_links || []).map(link =>
                    updateDiscount.mutateAsync({
                        linkId: link.id,
                        groupId: id!,
                        discount: link.discount
                    })
                )
            );
            notify.success(t('success'), t('discounts_saved_success', 'Discounts saved successfully'));
        } catch (error) {
            notify.error(t('error'), t('error_saving_discounts', 'Error saving discounts'));
        } finally {
            setIsSavingDiscounts(false);
        }
    };

    const handleDistributeDiscount = async () => {
        if (!draft || typeof targetPrice !== 'number' || targetPrice < 0) return;

        const filteredLinks = (draft.service_links || []).filter(link => {
            const priceObj = getServiceSellPrice(link.service, dayjs().format('YYYY-MM-DD'))?.[0];
            return (priceObj?.default_amount || 0) > 0;
        });

        if (filteredLinks.length === 0) return;

        const activeBasePrice = filteredLinks.reduce((acc, link) => {
            const priceObj = getServiceSellPrice(link.service, dayjs().format('YYYY-MM-DD'))?.[0];
            return acc + (priceObj?.default_amount || 0);
        }, 0);

        if (activeBasePrice === 0) {
            notify.error(t('error'), t('base_price_zero', 'Base price is zero'));
            return;
        }

        const totalDiscountAmount = Math.max(0, activeBasePrice - targetPrice);
        let remainingTargetPrice = targetPrice;
        let processedActiveLinks = 0;

        const updatedLinks = (draft.service_links || []).map(link => {
            const priceObj = getServiceSellPrice(link.service, dayjs().format('YYYY-MM-DD'))?.[0];
            const linkBasePrice = priceObj?.default_amount || 0;

            if (linkBasePrice === 0) {
                return { ...link, discount: 0 };
            }

            processedActiveLinks++;
            let itemDiscountPercentage = 0;

            if (processedActiveLinks === filteredLinks.length) {
                // The last active item ensures we mathematically hit the remaining target price exactly
                itemDiscountPercentage = (1 - (remainingTargetPrice / linkBasePrice)) * 100;
            } else {
                let itemDiscountAmount = 0;
                if (distributeMode === 'equal') {
                    itemDiscountAmount = (linkBasePrice / activeBasePrice) * totalDiscountAmount;
                } else {
                    itemDiscountAmount = totalDiscountAmount / filteredLinks.length;
                }
                itemDiscountPercentage = (itemDiscountAmount / linkBasePrice) * 100;
            }

            itemDiscountPercentage = Math.max(0, Math.min(100, itemDiscountPercentage));

            // Round to 6 decimals to maintain extreme precision for calculation 
            // while avoiding extremely long JS repeating floats
            itemDiscountPercentage = Number(itemDiscountPercentage.toFixed(6));

            // Subtract the EXACT resolved mathematical price from the remaining target
            const exactItemFinalPrice = linkBasePrice * (1 - itemDiscountPercentage / 100);
            remainingTargetPrice -= exactItemFinalPrice;

            return { ...link, discount: itemDiscountPercentage };
        });

        setDraft({ ...draft, service_links: updatedLinks });
    };

    const selectedServiceLinks = (draft?.service_links || []).sort(i => i.id);

    // Calculate total price using helper
    const {
        amount: price_amount,
        currency: price_currency
    } = draft ? getServiceGroupTotalPrice(draft) : { amount: 0, currency: '---' };

    setWebTitle(`${t('service_group')} ${serviceGroup?.name || ''}`);

    return (
        <div>
            <div className="panel-content">
                {
                    isLoadingServiceGroup
                        ? <SkeletonList />
                        : (
                            <div className="space-y-4">
                                <div className="solid-box-border space-y-4 h-fit">
                                    <TextInput
                                        label={t('name')}
                                        value={draft.name}
                                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                        className="font-semibold"
                                        size="sm"
                                    />
                                    <div className='flex gap-2'>
                                        <ImageUploadPreview
                                            label={t('cover_image')}
                                            value={draft.cover_url}
                                            uploadKey={`service-groups/${id}`}
                                            height={200}
                                            placeholder={t('upload_cover_image')}
                                            onChange={(url) => setDraft({ ...draft, cover_url: url })}
                                            uploadUrl={`/service-groups/${id}/cover`}
                                            onRemove={() => {
                                                deleteCover.mutate(id!, {
                                                    onSuccess: () => {
                                                        setDraft({ ...draft, cover_url: undefined }); // or null/empty string
                                                        notify.success(t('cover_removed'), t('cover_removed_success'));
                                                    },
                                                    onError: () => {
                                                        notify.error(t('cover_removed'), t('cover_removed_error'));
                                                    }
                                                });
                                            }}
                                        />
                                        <Textarea
                                            className='w-full'
                                            autosize
                                            minRows={4}
                                            label={t('observations')}
                                            value={draft.observations || ''}
                                            onChange={(e) => setDraft({ ...draft, observations: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <Button
                                            loading={update.isPending}
                                            onClick={saveServiceGroup}
                                            size="xs"
                                            radius="lg"
                                            color="black"
                                        >
                                            {t('save')}
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <div className="space-y-4">
                                        <Button
                                            size="xs"
                                            radius="md"
                                            color="black"
                                            leftSection={<IconPlus size={16} />}
                                            onClick={() => setDrawerOpened(true)}
                                        >
                                            {t('add_service_to_group')}
                                        </Button>
                                        {Object.entries(
                                            selectedServiceLinks.reduce((acc, link) => {
                                                const cat = link.service.category || 'other';
                                                if (!acc[cat]) acc[cat] = [];
                                                acc[cat].push(link);
                                                return acc;
                                            }, {} as Record<string, typeof selectedServiceLinks>)
                                        )
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([category, links]) => (
                                                <div key={category}>
                                                    <div className="space-y-1">
                                                        {links.map(link => {
                                                            const priceObj = getServiceSellPrice(link.service, dayjs().format('YYYY-MM-DD'))?.[0];
                                                            const basePrice = priceObj?.default_amount || 0;
                                                            const discountedPrice = basePrice * (1 - (link.discount || 0) / 100);

                                                            return (
                                                                <div
                                                                    key={link.service_id}
                                                                    className="solid-box-border flex items-center justify-between gap-4"
                                                                >
                                                                    <ServiceCardCatalog
                                                                        service={link.service}
                                                                        hidePrices
                                                                        className='flex-2'
                                                                    />

                                                                    <div className="flex-1 flex items-center gap-6 bg-gray-100 rounded-lg p-4">
                                                                        {/* Price Breakdown */}
                                                                        <div className="flex flex-col items-end">
                                                                            {link.discount > 0 ? (
                                                                                <>
                                                                                    <div className="text-xs text-gray-400 italic">
                                                                                        {formatPrice(priceObj?.default_amount)} {priceObj?.default_currency}
                                                                                    </div>
                                                                                    <div className="text-sm font-bold text-green-600">
                                                                                        {formatPrice(discountedPrice)} {priceObj?.default_currency}
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="text-sm font-bold">
                                                                                    {formatPrice(basePrice)} {priceObj?.default_currency}
                                                                                </div>
                                                                            )}
                                                                            <div className="text-[10px] text-gray-400 lowercase mt-0.5 whitespace-nowrap">
                                                                                {priceObj?.start_day || priceObj?.end_day
                                                                                    ? `${priceObj.start_day ? formatDayShort(priceObj.start_day) : '*'} ${priceObj.end_day ? `- ${formatDayShort(priceObj.end_day)}` : ''}`
                                                                                    : t('always')}
                                                                            </div>
                                                                        </div>

                                                                        <NumberInput
                                                                            value={link.discount}
                                                                            onChange={(value) => handleUpdateDraftDiscount(link.id, Number(value))}
                                                                            min={0}
                                                                            max={100}
                                                                            step={1}
                                                                            leftSection={<IconPercentage size={12} />}
                                                                            size="sm"
                                                                            className="w-24"
                                                                            hideControls
                                                                        />
                                                                        <Button
                                                                            size="xs"
                                                                            variant="subtle"
                                                                            color="red"
                                                                            className="ml-2"
                                                                            loading={removeService.isPending}
                                                                            onClick={() => removeServiceFromGroup(link.service)}
                                                                        >
                                                                            <IconTrash size={16} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                                <div className="solid-box-border flex items-center gap-2 justify-between">

                                    <div className="flex items-center gap-2">
                                        <NumberInput
                                            placeholder={t('target_price')}
                                            value={targetPrice}
                                            onChange={(val) => setTargetPrice(typeof val === 'number' ? val : '')}
                                            size="xs"
                                            className="w-32"
                                            hideControls
                                            min={0}
                                            decimalScale={2}
                                        />
                                        <SegmentedControl
                                            value={distributeMode}
                                            onChange={(val) => setDistributeMode(val as 'proportional' | 'equal')}
                                            data={[
                                                { value: 'proportional', label: t('proportional') },
                                                { value: 'equal', label: t('equal') }
                                            ]}
                                            size="xs"
                                        />
                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="blue"
                                            onClick={handleDistributeDiscount}
                                            disabled={typeof targetPrice !== 'number' || targetPrice < 0}
                                        >
                                            {t('distribute')}
                                        </Button>
                                    </div>
                                    <div className="flex flex-col items-end justify-end">
                                        <div className="solid-box-border-green flex items-center gap-2 h-full">
                                            Total: <MoneyIn amount={price_amount} currency={price_currency} />
                                            <div className="text-[10px] text-gray-400 lowercase pr-2 mt-0.5">
                                                ({formatDay(dayjs().format('YYYY-MM-DD'))})
                                            </div>
                                            <Button
                                                size="xs"
                                                color="black"
                                                radius="lg"
                                                loading={isSavingDiscounts}
                                                onClick={handleSaveAllDiscounts}
                                                className="ml-2"
                                            >
                                                {t('save')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                }

                {/* Drawer for selecting services */}
                <DrawerPage
                    size='80%'
                    opened={drawerOpened}
                    onClose={() => setDrawerOpened(false)}
                    title={t('select_service_for_group')}
                >
                    <ServicesListWithFilters
                        onSelect={addServiceToGroup}
                    />
                </DrawerPage>
            </div>

            <Button
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteServiceGroup}
                loading={deleteServiceGroup.isPending}
            >
                {t('delete_service_group')}
            </Button>
        </div>
    );
}
